import React, { useCallback, useEffect, useState } from "react";
import { ChefHat, WifiOff } from "lucide-react";
import EmptyState from "../../CommonComponents/EmptyState";
import SearchInput from "../../CommonComponents/SearchInput";
import Pagination from "../../CommonComponents/Pagination";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from "../../../utils/tableStyle";
import { fetchProductions } from "../../../services/productionServices";
import { authStorage } from "../../../utils/authStorage";
import axiosInstance from "../../../services/axiosInstance";

const ProductionLogsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("summary");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [branches, setBranches] = useState([]);
  const [activeBranchTab, setActiveBranchTab] = useState("");

  const rowsPerPage = 10;
  const user = authStorage.getUser();
  const isAdminView = Number(user?.user_type) === 1;

  const loadBranches = useCallback(async () => {
    if (!isAdminView) return;
    try {
      const res = await axiosInstance.get("/admin/branches");
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const active = data.filter((branch) => String(branch?.status || "ACTIVE") === "ACTIVE");
      setBranches(active);
      if (!active.length) {
        setActiveBranchTab("");
        return;
      }
      if (!activeBranchTab || !active.some((b) => String(b.id) === String(activeBranchTab))) {
        setActiveBranchTab(String(active[0].id));
      }
    } catch (err) {
      setBranches([]);
    }
  }, [isAdminView, activeBranchTab]);

  const selectedBranchId = isAdminView ? Number(activeBranchTab || "") : "";

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchProductions({
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm,
        startDate: appliedStartDate,
        endDate: appliedEndDate,
        view: viewMode,
        branchId: selectedBranchId,
      });

      setRows(Array.isArray(response?.data) ? response.data : []);
      setTotalPages(Math.max(Number(response?.pagination?.totalPages) || 1, 1));
      setTotalCount(Number(response?.pagination?.total) || 0);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, appliedStartDate, appliedEndDate, rowsPerPage, viewMode, selectedBranchId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  const formatTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleApplyDate = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setCurrentPage(1);
  };

  const handleClearDate = () => {
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setCurrentPage(1);
  };

  return (
    <div className="pt-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ color: "var(--color-text)" }}>Production Logs</h2>
        <p className="text-sm text-gray-500">Track production history using summary or detailed view.</p>
      </div>

      {isAdminView && (
        <div className="mb-4">
          <p className="text-sm font-semibold mb-2" style={{ color: "var(--color-text)" }}>Branches</p>
          <div className="flex gap-1 flex-wrap">
            {branches.map((branch) => (
              <button
                key={branch.id}
                type="button"
                className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${String(activeBranchTab) === String(branch.id)
                  ? "bg-[var(--color-primary)] text-[var(--color-on-primary)] border-[var(--color-primary)]"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                onClick={() => {
                  setActiveBranchTab(String(branch.id));
                  setCurrentPage(1);
                }}
              >
                {branch.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Filter</h3>
        <div className="flex items-center gap-3 bg-[#f6f6f8] p-4 rounded-xl">
          <label className="text-sm font-semibold text-gray-700">Select Date Range:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate || undefined}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate || undefined}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
          />
          {(startDate && endDate) && (
            <button
              onClick={handleApplyDate}
              className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Submit
            </button>
          )}
          {(appliedStartDate || appliedEndDate) && (
            <button
              onClick={handleClearDate}
              className="text-sm font-semibold text-primary hover:text-primary-dark"
            >
              Clear
            </button>
          )}
          {!appliedStartDate && !appliedEndDate && (
            <span className="text-sm text-gray-500 italic">Showing all dates</span>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <SearchInput
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search product..."
        />
        <div className="flex gap-2">
          <button
            type="button"
            className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${viewMode === "summary" ? "text-white" : "text-gray-700 bg-gray-100 hover:bg-gray-200"}`}
            style={viewMode === "summary" ? { background: "var(--color-primary)" } : {}}
            onClick={() => {
              setViewMode("summary");
              setCurrentPage(1);
            }}
          >
            Daily Summary
          </button>
          <button
            type="button"
            className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${viewMode === "detailed" ? "text-white" : "text-gray-700 bg-gray-100 hover:bg-gray-200"}`}
            style={viewMode === "detailed" ? { background: "var(--color-primary)" } : {}}
            onClick={() => {
              setViewMode("detailed");
              setCurrentPage(1);
            }}
          >
            Detailed Logs
          </button>
        </div>
      </div>

      {error ? (
        <EmptyState
          icon={WifiOff}
          title="Failed to Load Production Logs"
          message="Check your connection and try again."
          onRetry={loadLogs}
          retryLabel="Reload"
        />
      ) : loading ? (
        <div className="text-center py-8 text-gray-500">Loading production logs...</div>
      ) : !rows.length ? (
        <EmptyState
          icon={ChefHat}
          title="No Production Logs"
          message="No production records found for your current filter."
        />
      ) : (
        <>
          <div className={tableContainer}>
            <table className={tableBase}>
              <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                {viewMode === "summary" ? (
                  <tr>
                    <th className={tableHeader + " text-center"}>Date</th>
                    {isAdminView && <th className={tableHeader + " text-center"}>Branch</th>}
                    <th className={tableHeader + " text-center"}>Product</th>
                    <th className={tableHeader + " text-center"}>Total Produced</th>
                    <th className={tableHeader + " text-center"}>Batches</th>
                    <th className={tableHeader + " text-center"}>Unit</th>
                  </tr>
                ) : (
                  <tr>
                    <th className={tableHeader + " text-center"}>Date</th>
                    <th className={tableHeader + " text-center"}>Time</th>
                    {isAdminView && <th className={tableHeader + " text-center"}>Branch</th>}
                    <th className={tableHeader + " text-center"}>Product</th>
                    <th className={tableHeader + " text-center"}>Qty Produced</th>
                    <th className={tableHeader + " text-center"}>Unit</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {viewMode === "summary"
                  ? rows.map((row) => (
                    <tr key={`${row.production_date}-${row.product_id}-${row.branch_id || "all"}`} className={tableRow + " border-b last:border-0"}>
                      <td className={tableCell + " text-center"}>{formatDate(row.production_date)}</td>
                      {isAdminView && <td className={tableCell + " text-center"}>{row.branch_name || "-"}</td>}
                      <td className={tableCell + " text-center"}>{row.product_name || "-"}</td>
                      <td className={tableCell + " text-center tabular-nums"}>{Number(row.total_qty) || 0}</td>
                      <td className={tableCell + " text-center tabular-nums"}>{Number(row.batches) || 0}</td>
                      <td className={tableCell + " text-center"}>{row.unit_type || "pcs"}</td>
                    </tr>
                  ))
                  : rows.map((production) => (
                    <tr key={production.id} className={tableRow + " border-b last:border-0"}>
                      <td className={tableCell + " text-center"}>{formatDate(production.created_at || production.createdAt)}</td>
                      <td className={tableCell + " text-center"}>{formatTime(production.created_at || production.createdAt)}</td>
                      {isAdminView && <td className={tableCell + " text-center"}>{production.branch?.name || "-"}</td>}
                      <td className={tableCell + " text-center"}>{production.product?.name || "-"}</td>
                      <td className={tableCell + " text-center tabular-nums"}>{Number(production.qty) || 0}</td>
                      <td className={tableCell + " text-center"}>{production.product?.unit_type || "pcs"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col items-center gap-2 mt-3">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
            <p className="text-sm" style={{ color: "var(--color-text)", opacity: 0.7 }}>
              Showing {totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} -{" "}
              {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} items | Page {currentPage} of {totalPages}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductionLogsPage;
